import { GRID_COLS, GRID_ROWS } from '../types'

/**
 * Calculates the optimal cell size for the grid based on viewport dimensions.
 * Ensures the entire grid fits on screen without scrolling.
 * @param viewportWidth  - window.innerWidth
 * @param viewportHeight - window.innerHeight (optional, enables vertical fit)
 * @param reservedY      - vertical space reserved for header/footer (px)
 */
export function calculateCellSize(
  viewportWidth: number,
  viewportHeight?: number,
  reservedY: number = 0,
): number {
  const baseCellSize = 24
  const baseGridWidth = baseCellSize * GRID_COLS
  
  // Use consistent padding across all screen sizes
  const padding = viewportWidth < 640 ? 16 : 32
  const availableWidth = Math.max(0, viewportWidth - padding * 2)
  
  // Width-based scale
  const scaleW = availableWidth / baseGridWidth

  // Height-based scale (if viewport height provided)
  let scaleH = Infinity
  if (viewportHeight && viewportHeight > 0) {
    const availableHeight = Math.max(0, viewportHeight - reservedY)
    const baseGridHeight = baseCellSize * GRID_ROWS
    scaleH = availableHeight / baseGridHeight
  }

  const scale = Math.min(1, scaleW, scaleH)
  
  // Apply scale to base cell size, with minimum of 8px
  const scaled = baseCellSize * scale
  const snapped = Math.floor(scaled)
  return Math.max(8, snapped)
}

/**
 * Calculates dynamic padding/margin based on cell size.
 * This ensures card internals scale proportionally.
 */
export function getCardPadding(cellSize: number): number {
  // Base padding is 16px at cellSize=24
  return Math.max(8, (cellSize / 24) * 16)
}

/**
 * Calculates dynamic font size based on cell size and base size.
 */
export function getScaledFontSize(cellSize: number, baseSize: number): number {
  return Math.max(10, (cellSize / 24) * baseSize)
}

/**
 * Calculates dynamic icon size based on cell size.
 */
export function getScaledIconSize(cellSize: number, baseSize: number = 24): number {
  return Math.max(16, (cellSize / 24) * baseSize)
}
